import { useState } from 'react';
import usePythonFunctions from 'api/python-functions/usePythonFunctions';
import usePythonFunctionFolders from 'api/python-functions/usePythonFunctionFolders';
import FormBlock from 'components/form/FormBlock';
import FormButtons from 'components/form/FormButtons';
import { getFormSchema, getUiSchema, validator } from 'api/python-functions/formSchema';
import { folderExtraErrors, getFolderPathServerError } from 'utils/folderPath';
import { CreatePythonFunctionRequest } from 'api/model';
import useProjects from 'api/projects/useProjects';
import Loader from 'components/Loader';
import PythonFunctionTestRun from './PythonFunctionTestRun';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';

const FORM_ID = 'python-add-form';

type Props = {
  projectId: string;
  inputCount?: number;
  initialFolderPath?: string;
  onCancel: () => void;
  onSubmit: (functionId: string) => void;
};

const PythonFunctionAdd = ({ projectId, inputCount, initialFolderPath, onCancel, onSubmit }: Props) => {
  const {
    create: { isPending, mutateAsync },
  } = usePythonFunctions(projectId);
  const { projects, isLoading: areProjectsLoading } = useProjects();
  const { folders } = usePythonFunctionFolders(projectId || null, true);

  const [formData, setFormData] = useState<CreatePythonFunctionRequest | null>(null);
  const [folderError, setFolderError] = useState<string | null>(null);

  const handleSubmit = async (data: CreatePythonFunctionRequest) => {
    setFolderError(null);
    try {
      const { id: createdId } = await mutateAsync(data);
      onSubmit(createdId);
    } catch (error) {
      setFolderError(getFolderPathServerError(error));
    }
  };

  if (areProjectsLoading) {
    return <Loader />;
  }

  return (
    <SettingsFormShell
      title="Add Python Function"
      footer={<FormButtons formId={FORM_ID} onCancel={onCancel} isLoading={isPending} />}
    >
      <FormBlock
        onSubmit={(data) => void handleSubmit(data)}
        onCancel={onCancel}
        onChange={setFormData}
        schema={getFormSchema(projects, projectId)}
        uiSchema={getUiSchema(folders)}
        initialValues={
          initialFolderPath ? ({ folder_path: initialFolderPath } as CreatePythonFunctionRequest) : undefined
        }
        isLoading={isPending}
        formId={FORM_ID}
        validator={validator}
        extraErrors={folderExtraErrors(folderError)}
        showButtons={false}
      />
      <PythonFunctionTestRun
        functionCode={formData?.function_code}
        extraLibs={formData?.extra_libs}
        inputCount={inputCount}
      />
    </SettingsFormShell>
  );
};

export default PythonFunctionAdd;
